import Moment from "moment";
import _ from "underscore";

import * as DateUtil from "@travellocal/utils/dateHelpers";
import { neverHere } from "@travellocal/utils/tsHelpers";

export class EntityConverter {

  public static excursionToNewExcursionInput(excursion: Partial<IExcursion>): INewExcursion {
    const newExcursion: INewExcursion = {
      eventTypeId: ComponentType.Excursion,
      name: excursion.name,
      description: excursion.description,
      ltoId: excursion.ltoId,
      imageIds: excursion.images ? _.map(excursion.images, (image) => image.id) : undefined,
      destinationId: excursion.destination ? excursion.destination.id : 0,
      statusId: excursion.status,
      shared: excursion.shared,
      arrangedByCustomer: excursion.arrangedByCustomer,
    };

    return newExcursion;
  }

  public static excursionToExcursionUpdateInput(excursion: Partial<IExcursion>): IExcursionUpdate {
    const excursionUpdate: IExcursionUpdate = {
      eventTypeId: ComponentType.Excursion,
      id: excursion.id,
      name: excursion.name,
      description: excursion.description,
      ltoId: excursion.ltoId ? excursion.ltoId : null,
      imageIds: excursion.images ? _.map(excursion.images, (image) => image.id) : undefined,
      destinationId: excursion.destination ? excursion.destination.id : 0,
      statusId: excursion.status,
      shared: excursion.shared,
      arrangedByCustomer: excursion.arrangedByCustomer,
    };

    return excursionUpdate;
  }

  public static transferToNewTransferInput(transfer: Partial<ITransfer>): INewTransfer {
    const newTransfer: INewTransfer = {
      eventTypeId: ComponentType.Transfer,
      name: transfer.name,
      description: transfer.description,
      ltoId: transfer.ltoId,
      imageIds: _.map(transfer.images, (image) => image.id),
      fromDestinationId: transfer.fromDestination ? transfer.fromDestination.id : 0,
      toDestinationId: transfer.toDestination ? transfer.toDestination.id : 0,
      statusId: transfer.status,
      transferTypeId: transfer.transferTypeId || null, // 0 is used in <SelectInput> options to represent null
      shared: transfer.shared,
      arrangedByCustomer: transfer.arrangedByCustomer,
      serviceProvider: transfer.serviceProvider,
      serviceCode: transfer.serviceCode,

    };

    return newTransfer;
  }

  public static transferToTransferUpdateInput(transfer: Partial<ITransfer>): ITransferUpdate {
    const transferUpdate: ITransferUpdate = {
      eventTypeId: ComponentType.Transfer,
      id: transfer.id,
      name: transfer.name,
      description: transfer.description,
      imageIds: _.map(transfer.images, (image) => image.id),
      fromDestinationId: transfer.fromDestination ? transfer.fromDestination.id : 0,
      toDestinationId: transfer.toDestination ? transfer.toDestination.id : 0,
      statusId: transfer.status,
      transferTypeId: transfer.transferTypeId || null, // 0 is used in <SelectInput> options to represent null
      shared: transfer.shared,
      arrangedByCustomer: transfer.arrangedByCustomer,
      serviceProvider: transfer.serviceProvider,
      serviceCode: transfer.serviceCode,
    };

    return transferUpdate;
  }

  public static itineraryDayEventToItineraryDayEventUpdateInput(itineraryDayEvent: IItineraryDayEvent): IItineraryDayEventUpdateInput {
    return {
      id: itineraryDayEvent.id,
      shared: itineraryDayEvent.shared,
      arrangedByCustomer: itineraryDayEvent.arrangedByCustomer,
      notes: itineraryDayEvent.notes,
      serviceProvider: itineraryDayEvent.serviceProvider,
      serviceCode: itineraryDayEvent.serviceCode,
    };
  }

  public static sampleItinerarySummaryToInputUpdate(sampleItinerarySummary: ISampleItinerarySummary): ISampleItineraryUpdateInput {
    return {
      slug: sampleItinerarySummary.slug,
      description: sampleItinerarySummary.description,
      name: sampleItinerarySummary.name,
      priceBasedOn: sampleItinerarySummary.priceBasedOn,
      pricePerPerson: sampleItinerarySummary.pricePerPerson ? sampleItinerarySummary.pricePerPerson.price : 0,
      primaryLocationId: sampleItinerarySummary.primaryLocation ? sampleItinerarySummary.primaryLocation.id : 0,
      themeIds: _.map(sampleItinerarySummary.themes, (theme) => theme.id),
      titleTag: sampleItinerarySummary.titleTag,
      descriptionTag: sampleItinerarySummary.descriptionTag,
      heroImageId: sampleItinerarySummary.heroImage ? sampleItinerarySummary.heroImage.id : null,
      isGroupTour: sampleItinerarySummary.isGroupTour,
      dayOrder: sampleItinerarySummary.dayOrder,
    };
  }

  public static enquiryItineraryLineItemToNewEnquiryItineraryLineItemInput(
    enquiryItineraryId: number,
    enquiryItineraryLineItem: IEnquiryItineraryLineItem,
  ): INewEnquiryItineraryLineItemInput {
    return {
      name: enquiryItineraryLineItem.name,
      enquiryItineraryId,
      quantity: enquiryItineraryLineItem.quantity,
      unitPrice: enquiryItineraryLineItem.unitPrice.price,
    };
  }

  public static enquiryItineraryLineItemToIEnquiryItineraryLineItemUpdateInput(
    enquiryItineraryId: number,
    enquiryItineraryLineItem: IEnquiryItineraryLineItem,
  ): IEnquiryItineraryLineItemUpdateInput {
    return {
      id: enquiryItineraryLineItem.id,
      name: enquiryItineraryLineItem.name,
      quantity: enquiryItineraryLineItem.quantity,
      unitPrice: enquiryItineraryLineItem.unitPrice.price,
    };
  }

  public static enquiryItinerarySummaryToInputUpdate(enquiryItinerarySummary: IEnquiryItinerarySummary): IEnquiryItineraryUpdateInput {
    const update = {
      id: enquiryItinerarySummary.id,
      primaryLocationId: enquiryItinerarySummary.primaryLocation ? enquiryItinerarySummary.primaryLocation.id : 0,
      numberOfPeople: enquiryItinerarySummary.numberOfPeople,
      startDate: enquiryItinerarySummary.startDate,
      draftMessage: enquiryItinerarySummary.draftMessage,
      heroImageId: enquiryItinerarySummary.heroImage ? enquiryItinerarySummary.heroImage.id : null,
    }

    // If the enquiry itinerary is external, we send the number of days
    // for the enquiryItineraryService to automatically add/remove days
    if (enquiryItinerarySummary.isExternal) {
      return {
        ...update,
        itineraryUrl: enquiryItinerarySummary.itineraryUrl,
        numberOfDays: enquiryItinerarySummary.numberOfDays,
      };
    }

    // Otherwise, send the dayOrder the user has set
    return {
      ...update,
      dayOrder: enquiryItinerarySummary.dayOrder,
    };
  }

  public static enquiryItinerarySummaryToIEnquiryItineraryPricingInformationUpdateInput(
    enquiryItinerarySummary: IEnquiryItinerarySummary,
  ): IEnquiryItineraryPricingInformationUpdateInput {
    const update = {
      id: enquiryItinerarySummary.id,
      includedServices: enquiryItinerarySummary.includedServices,
      excludedServices: enquiryItinerarySummary.excludedServices,
      prepayment: null,
    };

    if (enquiryItinerarySummary.prepayment) {
      update.prepayment = {
        amount: enquiryItinerarySummary.prepayment.amount.price,
        reason: enquiryItinerarySummary.prepayment.reason,
      };
    }
    return update;
  }

  public static accommodationToNewAccommodationInput(ltoId: number, accommodation: Partial<IAccommodation>): INewAccommodation {
    return {
      description: accommodation.description,
      destinationId: accommodation.destination ? accommodation.destination.id : 0,
      name: accommodation.name,
      url: accommodation.url,
      ltoId,
      statusId: accommodation.status,
    };
  }

  public static accommodationToAccommodationUpdateInput(accommodation: Partial<IAccommodation>): IAccommodationUpdate {
    return {
      id: accommodation.id,
      description: accommodation.description,
      destinationId: accommodation.destination ? accommodation.destination.id : 0,
      name: accommodation.name,
      url: accommodation.url,
      statusId: accommodation.status,
      ltoId: accommodation.ltoId ? accommodation.ltoId : null,
    };
  }

  public static itineraryDayAccommodationToItineraryDayAccommodationUpdate(itineraryDayAccommodation: IItineraryDayAccommodation): IItineraryDayAccommodationUpdate {
    return {
      id: itineraryDayAccommodation.id,
      includesBreakfast: itineraryDayAccommodation.includesBreakfast,
      includesDinner: itineraryDayAccommodation.includesDinner,
      includesLunch: itineraryDayAccommodation.includesLunch,
      notes: itineraryDayAccommodation.notes,
      roomDescription: itineraryDayAccommodation.roomDescription,
    };
  }

  public static locationToNewLocationInput(location: ILocation): INewLocationInput {
    const commonFields: INewLocationInputBase = {
      name: location.name,
      slug: location.slug,
      descriptionHtml: location.descriptionHtml,
      geoLocation: location.geoLocation,
      localName: location.localName,
    };

    switch (location.locationType) {
      case LocationType.Region:
        const region: INewRegionInput = {
          locationType: LocationType.Region,
          ...commonFields,
        };
        return region;
      case LocationType.Country:
        const country: INewCountryInput = {
          locationType: LocationType.Country,
          ...commonFields,
          regionId: location.parent.id,
        };
        return country;
      case LocationType.Destination:
        const destination: INewDestinationInput = {
          locationType: LocationType.Destination,
          ...commonFields,
          countryId: location.parent.id,
        };
        return destination;
      case LocationType.Airport:
        const airport: IAirportUpdateInput = {
          id: undefined,
          locationType: LocationType.Airport,
          ...commonFields,
          cityId: location.parent?.id || undefined,
          countryId: location.parent && location.parent.parent ? location.parent.parent.id : undefined,
          iataCode: location.iataCode?.length ? location.iataCode : undefined,
        };
        return airport;
      default:
        neverHere(location, "location type newlocation entity converter");
    }
  }

  public static locationToLocationUpdateInput(location: ILocation): ILocationUpdateInput {
    const commonFields: ILocationUpdateInputBase = {
      id: location.id,
      name: location.name,
      slug: location.slug,
      descriptionHtml: location.descriptionHtml,
      titleTag: location.titleTag,
      descriptionTag: location.descriptionTag,
      ltoPageTitleTag: location.ltoPageTitleTag,
      ltoPageDescriptionTag: location.ltoPageDescriptionTag,
      galleryPageTitleTag: location.galleryPageTitleTag,
      galleryPageDescriptionTag: location.galleryPageDescriptionTag,
      geoLocation: location.geoLocation,
      localName: location.localName,
    };

    switch (location.locationType) {
      case LocationType.Region:
        const region: IRegionUpdateInput = {
          locationType: LocationType.Region,
          ...commonFields,
        };
        return region;
      case LocationType.Country:
        const country: ICountryUpdateInput = {
          locationType: LocationType.Country,
          ...commonFields,
          regionId: location.parent.id,
        };
        return country;
      case LocationType.Destination:
        const destination: IDestinationUpdateInput = {
          locationType: LocationType.Destination,
          ...commonFields,
          countryId: location.parent.id,
        };
        return destination;
      case LocationType.Airport:
        const airport: IAirportUpdateInput = {
          locationType: LocationType.Airport,
          ...commonFields,
          cityId: location.parent ? location.parent.id : null,
          countryId: location.parent && location.parent.parent ? location.parent.parent.id : null,
          iataCode: location.iataCode?.length ? location.iataCode : null,
        };
        return airport;
      default:
        neverHere(location, "location type locationupdate entity converter");
    }
  }

  public static locationToLocationWeatherUpdateInput(location: ILocation, parentLocationId?: number): ILocationWeatherUpdateInput {
    return {
      id: location.id,
      weather: location.weather,
      whenToGo: location.whenToGo,
    };
  }

  public static ltoToLtoSettingsUpdateInput(lto: ILto): ILtoSettingsUpdateInput {
    return {
      id: lto.id,
      slug: lto.slug,
      commissionRate: lto.commissionRate,
      currencyId: lto.currency.id,
      minimumPerDayPrice: lto.minimumPerDayPrice,
      paymentDueBeforeDepartureDays: lto.paymentDueBeforeDepartureDays,
      titleTag: lto.titleTag,
      descriptionTag: lto.descriptionTag,
      externalItineraries: lto.externalItineraries,
    };
  }

  public static editableLtoEmployeeToILtoEmployeeUpdateInput(ltoEmployee: IEditableLtoEmployee): ILtoEmployeeUpdate {
    return {
      id: ltoEmployee.id,
      firstName: ltoEmployee.firstName,
      lastName: ltoEmployee.lastName,
      isMasterUser: ltoEmployee.isMasterUser,
      phoneNumber: ltoEmployee.phoneNumber,
      skypeId: ltoEmployee.skypeId,
      emailAddress: ltoEmployee.emailAddress, // TODO api doesn't actually do anything with this
    };
  }

  public static userSummaryToUserUpdate(user: IUserSummary): IUserUpdate {
    return {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      image: user.image,
      skypeId: user.skypeId,
      emailAddress: user.emailAddress,
      lastSeen: user.lastSeen,
      canReceiveEmails: user.canReceiveEmails,
      canReceiveNotifications: user.canReceiveNotifications,
      canReceiveMessages: user.canReceiveMessages,
      locationId: (user.location != null) ? user.location.id : null,
      title: user.title,
      profileHtml: user.profileHtml,
    };
  }

  private static contentJson(contentWrap: Partial<IContent>): IContentInput["content"] {
    const { type, content } = contentWrap;
    const contentOut: IContentInput["content"] = { html: null };
    switch (type) {
      case ContentType.LocationSection:
        const locationSection = content as IContentLocationSectionInput["content"];
        if (locationSection.navTitle) { contentOut.navTitle = locationSection.navTitle; }
      // fall through...
      case ContentType.BlogPost:
        const blogPost = content as IContentBlogPostInput["content"];
        if (blogPost && blogPost.html) { contentOut.html = blogPost.html; }
    }
    return contentOut;
  }

  public static contentToCreateInput(contentWrap: IContentInput): IContentInput {
    const {
      authorId, guestAuthor, themeIds, locationIds, months, heroImageId, heroImageUrl, content, meta, status, publishedTimestamp, title, slug, type,
    } = contentWrap;

    const updateInput = {
      title,
      slug,
      type,
      locationIds: locationIds || [],
      themeIds: themeIds || [],
      authorId,
      guestAuthor,
      months: months != null ? months : [],
      meta: meta != null ? meta : {} as IContentMetaInput,
      status: status != null ? status : PublishStatus.Draft,
      heroImageId,
      heroImageUrl, // Compat with legacy blog posts, if id is set then this is ignored.
      content: null,
      publishedTimestamp: status === PublishStatus.Live ? publishedTimestamp || DateUtil.now() : publishedTimestamp,
    } as IContentInput;

    switch (contentWrap.type) {
      case ContentType.BlogPost:
      case ContentType.LocationSection:
        updateInput.content = this.contentJson(contentWrap);
    }

    return updateInput;
  }

  public static contentToUpdateInput(contentWrap: Partial<IContent>): IContentInput {
    const {
      author, guestAuthor, themes, locations, months, heroImage, meta, status, publishedTimestamp, title, slug, type, id,
    } = contentWrap;

    const updateInput = {
      id,
      title,
      slug,
      type,
      locationIds: locations != null ? locations.map(l => l.id) : [],
      themeIds: themes != null ? themes.map(t => t.id) : [],
      months: months != null ? months : [],
      meta: meta != null ? meta : {} as IContentMetaInput,
      status: status != null ? status : PublishStatus.Draft,
      heroImageId: heroImage != null ? heroImage.id : null,
      heroImageUrl: heroImage != null ? heroImage.largeUrl : null, // Compat with legacy blog posts, if id is set then this is ignored.
      content: null,
      publishedTimestamp: status === PublishStatus.Live ? publishedTimestamp || DateUtil.now() : publishedTimestamp,
    } as IContentInput;

    switch (contentWrap.type) {
      case ContentType.LocationSection:
      case ContentType.BlogPost:
        updateInput.content = this.contentJson(contentWrap);
    }
    // Sample itineraries and reviews - content editor does not touch nested content field so no need to send anything

    if (author != null && author.id != null) {
      updateInput.authorId = author.id;
    }
    else {
      updateInput.guestAuthor = guestAuthor ? guestAuthor.trim() : null;
    }

    return updateInput;
  }

  public static themeToUpdateInput(theme: ITheme): IThemeInput {
    return {
      name: theme.name,
      icon: theme.icon,
      inspiration: theme.inspiration,
      enquiry: theme.enquiry,
    };
  }

  public static locationImagetoLocationImageUpdateInput(image: ILocationImage): ILocationImageUpdate {
    return {
      id: image.id,
      description: image.description,
      meta: image.meta,
      colour: image.colour,
    };
  }

  public static currencyToExchangeRatesUpdateInput(rates: IExchangeRatesEdit): ICustomerLtoExchangeRatesUpdateInput {
    return {
      currencyId: rates.id,
      customerExchangeRateAdjustment: rates.availableToCustomers ? rates.customerExchangeRateAdjustmentPercent : null,
      ltoExchangeRate: rates.ltoExchangeRate,
    };
  }

  public static checkForNullFlight(flightDetails: IFlightDetails) {
    if (!flightDetails) {
      return null;
    }
    if (!flightDetails.arrivalTime && !flightDetails.departureTime && !flightDetails.carrier && !flightDetails.number) {
      return null;
    }
    return flightDetails;
  }

  public static travelerDetailsToTravelerDetailsUpdateInput(travelerDetails: ITravelerDetails): ITravelerDetailsUpdateInput {
    return {
      dateOfBirth: travelerDetails.dateOfBirth,
      dietaryRequirements: travelerDetails.dietaryRequirements,
      emailAddress: travelerDetails.emailAddress,
      firstName: travelerDetails.firstName,
      id: travelerDetails.id,
      lastName: travelerDetails.lastName,
      otherTitle: travelerDetails.otherTitle,
      outboundFlight: this.checkForNullFlight(travelerDetails.outboundFlight),
      returnFlight: this.checkForNullFlight(travelerDetails.returnFlight),
      passportExpiryDate: travelerDetails.passportExpiryDate,
      passportIssuingCountryId: travelerDetails.passportIssuingCountry ? travelerDetails.passportIssuingCountry.id : null,
      countryOfResidenceId: travelerDetails.countryOfResidence ? travelerDetails.countryOfResidence.id : null,
      passportNumber: travelerDetails.passportNumber,
      title: travelerDetails.title,
    };
  }

  public static leadTravelerDetailsToLeadTravelerDetailsUpdateInput(enquiryId: number, travelerDetails: Partial<ILeadTravelerDetails>): ILeadTravelerDetailsUpdateInput {
    return {
      dateOfBirth: travelerDetails.dateOfBirth,
      dietaryRequirements: travelerDetails.dietaryRequirements,
      otherTitle: travelerDetails.otherTitle,
      firstName: travelerDetails.firstName,
      lastName: travelerDetails.lastName,
      outboundFlight: this.checkForNullFlight(travelerDetails.outboundFlight),
      returnFlight: this.checkForNullFlight(travelerDetails.returnFlight),
      passportExpiryDate: travelerDetails.passportExpiryDate,
      passportIssuingCountryId: travelerDetails.passportIssuingCountry ? travelerDetails.passportIssuingCountry.id : null,
      countryOfResidenceId: travelerDetails.countryOfResidence ? travelerDetails.countryOfResidence.id : null,
      passportNumber: travelerDetails.passportNumber,
      title: travelerDetails.title,
      address: travelerDetails.address,
      enquiryId,
      homePhone: travelerDetails.homePhone,
      travelPhone: travelerDetails.travelPhone,
      emergencyContactName: travelerDetails.emergencyContactName,
      emergencyContactNumber: travelerDetails.emergencyContactNumber,
      travelInsuranceProvider: travelerDetails.travelInsuranceProvider,
      travelInsurancePolicyNumber: travelerDetails.travelInsurancePolicyNumber,
      travelInsuranceEmergencyNumber: travelerDetails.travelInsuranceEmergencyNumber,
      roomRequirements: travelerDetails.roomRequirements,
      whereYouHeardOfUs: travelerDetails.whereYouHeardOfUs,
    };
  }

  public static reviewToNewReviewInput(review: IReview): IReviewInput {
    return {
      customerLocation: review.customerLocation,
      easeOfUseRating: review.easeOfUseRating,
      enquiryId: review.enquiryId,
      favoritePart: review.favoritePart,
      guideComment: review.guideComment,
      guideRating: review.guideRating,
      improvementComment: review.improvementComment,
      howLikelyToRecommend: review.howLikelyToRecommend,
      locationAdvice: review.locationAdvice,
      ltoImprovements: review.ltoImprovements,
      ltoInteractionRating: review.ltoInteractionRating,
      ltoPositives: review.ltoPositives,
      overallComment: review.overallComment,
      overallRating: review.overallRating,
      reviewTitle: review.reviewTitle,
      transportationRating: review.transportationRating,
      valueRating: review.valueRating,
      whereNext: review.whereNext,
    };
  }

  public static enquiryForm3ToInitialEnquiryInput(formData: Forms.EnquiryForm): INewInitialEnquiryMessageInput | INewAnonymousInitialEnquiryMessageInput {
    if (!formData) {
      return null;
    }

    const common = {
      version: 3 as 3,

      locationId: formData.location
        ? formData.location.id
        : null,

      independenceLevel: formData.independenceLevel,

      tripType: formData.tripType,

      budgetPerPerson: formData.budget && !formData.budget.budgetUnknown
        ? formData.budget.budgetPerPerson
        : null,

      planningStage: formData.planningStage,
      themeIds: formData.themes
        ? formData.themes.themeIds
        : null,

      text: formData.notes,

      roomPreferences: formData.travellers
        ? formData.travellers.roomPreferences
        : null,

      currencyId: formData.currencyId,

      sampleItineraryId: formData.itineraryId,

      // If country requires phone number for enquiry
      phoneNumber: formData.account
        ? formData.account.phoneNumber
        : null,

      ltoId: formData.ltoId
        ? formData.ltoId
        : null,
    } as INewInitialEnquiryMessageInput & INewAnonymousInitialEnquiryMessageInput;

    if (formData.recommendedBy) {
      if (formData.recommendedBy.source === "other") {
        common.recommendedBy = formData.recommendedBy.otherSource;
      } else if (formData.recommendedBy.source === "press / media" && formData.recommendedBy.pressSource === "other press") {
        common.recommendedBy = formData.recommendedBy.otherPressSource;
      } else if (formData.recommendedBy.source === "press / media" && typeof formData.recommendedBy.pressSource !== "undefined") {
        common.recommendedBy = formData.recommendedBy.pressSource;
      } else {
        common.recommendedBy = formData.recommendedBy.source;
      }
    }

    if (formData.travellers) {
      // Convert traveller age representation to the api's format
      const ageRangeIds = [];
      for (const key of Object.keys(formData.travellers.ageRangeIds)) {
        const count = formData.travellers.ageRangeIds[key];
        for (let i = 0; i < count; i++) {
          ageRangeIds.push(parseInt(key, 10));
        }
      }

      common.ageRangeIds = ageRangeIds;
    }

    if (formData.travelDate) {
      common.travelDateFlexibility = formData.travelDate.travelDateFlexibility;
      common.preferredTravelNumberOfDays = formData.travelDate.preferredTravelNumberOfDays;

      switch (formData.travelDate.travelDateFlexibility) {
        case 1 as TravelDateFlexibility.ExactDates:
          common.preferredTravelExactDate = formData.travelDate.preferredTravelExactDate;
          break;
        case 2 as TravelDateFlexibility.RoughDates:
          common.preferredTravelMonth = formData.travelDate.preferredTravelMonth;
          break;
        case 3 as TravelDateFlexibility.Unknown:
          break;
        default:
          neverHere(formData.travelDate.travelDateFlexibility);
      }
    }

    // Don't send back props that don't have values
    for (const key of Object.keys(common)) {
      if (common[key] == null) {
        delete common[key];
      }
    }

    if (formData.account && formData.account.hasAccount) {
      const repeatInput: INewInitialEnquiryMessageInput = common;
      return repeatInput;
    }
    else {
      const anonInput = common as INewAnonymousInitialEnquiryMessageInput;

      if (formData.account) {
        anonInput.firstName = formData.account.firstName;
        anonInput.lastName = formData.account.lastName;
        anonInput.emailAddress = formData.account.email;
        anonInput.password = formData.account.password;
        anonInput.marketingOptOut = !formData.account.marketingOptIn;
      }

      if (formData.hubspotCookie) {
        anonInput.hubspotCookie = formData.hubspotCookie;
      }

      return anonInput;
    }
  }

  public static reviewFormToReviewInput(formData: Forms.ReviewForm): IReviewInput {
    return {
      ...formData.customerInfo,
      ...formData.overallFeedback,
      ...formData.travelLocalFeedback,
      ...formData.ltoFeedback,
    };
  }

  public static reviewToReviewForm(review: IReview): Forms.ReviewForm {
    const customerInfo: Forms.ReviewCustomerInfo = {
      customerName: review.customerName,
      createdTimestamp: review.createdTimestamp,
    };

    const overallFeedback: Forms.OverallFeedback = {
      customerLocation: review.customerLocation,
      reviewTitle: review.reviewTitle,
      overallRating: review.overallRating,
      overallComment: review.overallComment,
    };

    const travelLocalFeedback: Forms.TravelLocalFeedback = {
      easeOfUseRating: review.easeOfUseRating,
      howLikelyToRecommend: review.howLikelyToRecommend,
      whereNext: review.whereNext,
      improvementComment: review.improvementComment,
    };

    const ltoFeedback: Forms.LtoFeedback = {
      ltoInteractionRating: review.ltoInteractionRating,
      guideRating: review.guideRating,
      guideComment: review.guideComment,
      transportationRating: review.transportationRating,
      valueRating: review.valueRating,
      ltoPositives: review.ltoPositives,
      ltoImprovements: review.ltoImprovements,
      locationAdvice: review.locationAdvice,
      favoritePart: review.favoritePart,
    };

    return { customerInfo, overallFeedback, travelLocalFeedback, ltoFeedback };
  }
  public static snippetToSnippetInput(snippet: ISnippet): ISnippetInput {
    return {
      id: snippet.id,
      title: snippet.title,
      content: snippet.content,
      snippetStatusId: snippet.snippetStatusId,
      ltoId: snippet.lto.id,
    };
  }
}
